/**
 * Map recurring overhead onto CRM charge dates for the Forecast daily walk.
 * Kept outside pageForecast sync (uses overheadDateFilter + finances types).
 */

import type { OverheadExpense } from '../types/finances';
import type { CashFlowProjectionMonth } from './cashFlowProjection';
import {
  collectOverheadOccurrences,
  endOfMonth,
  startOfMonth,
} from './overheadDateFilter';

function toDayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Put recurring overhead on CRM charge dates (not even-spread). */
export function buildOverheadBurnByDayKey(
  expenses: OverheadExpense[],
  months: CashFlowProjectionMonth[],
  today: Date,
  activeHeadcountByMonthKey?: Map<string, number>,
): Map<string, number> {
  const out = new Map<string, number>();
  if (months.length === 0 || expenses.length === 0) return out;
  const rangeStart = startOfMonth(months[0].month);
  const rangeEnd = endOfMonth(months[months.length - 1].month);
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const rows = collectOverheadOccurrences(
    expenses,
    { start: rangeStart, end: rangeEnd },
    0,
    today,
  );
  for (const row of rows) {
    if (row.date < todayStart) continue;
    const dayKey = toDayKey(row.date);
    const monthKey = dayKey.slice(0, 7);
    const headcount = activeHeadcountByMonthKey?.get(monthKey) ?? 0;
    const amount = row.expense.scalesWith === 'per_active_employee'
      ? (Number(row.expense.amount) || 0) * headcount
      : (Number(row.expense.amount) || 0);
    if (amount <= 0) continue;
    out.set(dayKey, (out.get(dayKey) ?? 0) + Math.round(amount));
  }
  return out;
}
